import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

export interface TranscriptionRecord {
  id: string;
  transcript: string;
  audioBlob: Blob;
  duration: number;
  language: string;
  confidence?: number;
  timestamp: string;
  filename: string;
  fileSize: number;
  associatedWith?: {
    type: 'interaction' | 'evidence' | 'visit';
    id: string;
  };
}

export interface TranscriptionState {
  // Data
  transcriptions: TranscriptionRecord[];
  currentTranscription: TranscriptionRecord | null;
  
  // Loading states
  isRecording: boolean;
  isTranscribing: boolean;
  isUploading: boolean;
  
  // Error states
  error: string | null;
  
  // Configuration
  language: string;
  prompt: string | null;
  autoTranscribe: boolean;
  
  // Service status
  serviceAvailable: boolean;
  serviceHealthy: boolean;
  lastHealthCheck: string | null;
}

const initialState: TranscriptionState = {
  transcriptions: [],
  currentTranscription: null,
  
  isRecording: false,
  isTranscribing: false,
  isUploading: false,
  
  error: null,
  
  language: 'id', // Indonesian by default
  prompt: null,
  autoTranscribe: true,
  
  serviceAvailable: false,
  serviceHealthy: false,
  lastHealthCheck: null,
};

// Async thunks
export const transcribeAudio = createAsyncThunk(
  'transcription/transcribeAudio',
  async (params: {
    audioBlob: Blob;
    language?: string;
    prompt?: string;
    filename?: string;
    associatedWith?: {
      type: 'interaction' | 'evidence' | 'visit';
      id: string;
    };
  }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { transcription: TranscriptionState };
      const { audioBlob, language, prompt, filename, associatedWith } = params;
      
      console.log('🎤 Transcription request via client-side Whisper:', {
        filename: filename || `recording_${Date.now()}.webm`,
        language: language || state.transcription.language,
        size: audioBlob.size
      });

      // Import client-side transcription service
      const { transcriptionService } = await import('../../lib/transcriptionService');
      
      // Check if transcription is available
      if (!transcriptionService.isAvailable()) {
        throw new Error('OpenAI API key not configured. Please set VITE_OPENAI_API_KEY in your .env file and restart the dev server.');
      }

      const result = await transcriptionService.transcribeAudio(audioBlob, {
        language: language || state.transcription.language,
        prompt: prompt || state.transcription.prompt || undefined,
        response_format: 'text',
        temperature: 0
      });

      if (result.error) {
        throw new Error(result.error);
      }

      console.log('✅ Transcription result:', result);

      // Calculate duration from audio blob (approximate)
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const duration = Math.floor(audioBuffer.duration);

      // Format transcript with timestamp
      const { formatTranscriptionWithTimestamp, getCurrentTimestamp } = await import('../../utils/transcriptionHelpers');
      const rawTranscript = result.transcription;
      const formattedTranscript = formatTranscriptionWithTimestamp(rawTranscript);
      const timestamp = getCurrentTimestamp();

      const transcriptionRecord: TranscriptionRecord = {
        id: `transcription_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        transcript: formattedTranscript,
        audioBlob,
        duration,
        language: result.language || language || state.transcription.language,
        timestamp: new Date().toISOString(),
        filename: result.filename || filename || `recording_${timestamp}.webm`,
        fileSize: result.fileSize || audioBlob.size,
        associatedWith
      };

      return transcriptionRecord;
    } catch (error) {
      console.error('Transcription failed:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Transcription failed');
    }
  }
);

export const checkTranscriptionServiceHealth = createAsyncThunk(
  'transcription/checkHealth',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔍 Checking client-side transcription service health...');
      
      // Check client-side transcription service
      const { transcriptionService } = await import('../../lib/transcriptionService');
      const isAvailable = transcriptionService.isAvailable();
      
      console.log('✅ Client-side transcription service health:', { isAvailable });
      
      return {
        available: isAvailable,
        healthy: isAvailable, // If API key is available, service is healthy
        status: isAvailable ? 'ready' : 'api_key_missing',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Health check failed:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Health check failed');
    }
  }
);

export const uploadTranscriptionToDirectus = createAsyncThunk(
  'transcription/uploadToDirectus',
  async (params: {
    transcriptionId: string;
    visitId?: string;
    interactionId?: string;
  }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { transcription: TranscriptionState };
      const { transcriptionId, visitId, interactionId } = params;
      
      const transcription = state.transcription.transcriptions.find(t => t.id === transcriptionId);
      if (!transcription) {
        throw new Error('Transcription record not found');
      }

      // Create form data for evidence upload
      const formData = new FormData();
      formData.append('file', transcription.audioBlob, transcription.filename);
      
      // Add metadata
      const metadata = {
        type: 'audio',
        title: 'Voice Transcription',
        description: transcription.transcript,
        transcription: transcription.transcript,
        duration: transcription.duration,
        language: transcription.language,
        visit_id: visitId,
        interaction_id: interactionId,
        timestamp: transcription.timestamp
      };
      
      formData.append('metadata', JSON.stringify(metadata));

      const response = await fetch('/api/evidence/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upload transcription');
      }

      const result = await response.json();
      
      return {
        transcriptionId,
        evidenceId: result.data.id,
        uploadedAt: result.timestamp
      };
    } catch (error) {
      console.error('Failed to upload transcription:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Upload failed');
    }
  }
);

const transcriptionSlice = createSlice({
  name: 'transcription',
  initialState,
  reducers: {
    // Recording state management
    setRecordingState: (state, action: PayloadAction<boolean>) => {
      state.isRecording = action.payload;
      if (action.payload) {
        state.error = null; // Clear errors when starting recording
      }
    },
    
    // Configuration
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload;
    },
    
    setPrompt: (state, action: PayloadAction<string | null>) => {
      state.prompt = action.payload;
    },
    
    setAutoTranscribe: (state, action: PayloadAction<boolean>) => {
      state.autoTranscribe = action.payload;
    },
    
    // Current transcription management
    setCurrentTranscription: (state, action: PayloadAction<TranscriptionRecord | null>) => {
      state.currentTranscription = action.payload;
    },
    
    // Transcription list management
    addTranscription: (state, action: PayloadAction<TranscriptionRecord>) => {
      state.transcriptions.unshift(action.payload); // Add to beginning
    },
    
    removeTranscription: (state, action: PayloadAction<string>) => {
      state.transcriptions = state.transcriptions.filter(t => t.id !== action.payload);
      if (state.currentTranscription?.id === action.payload) {
        state.currentTranscription = null;
      }
    },
    
    clearTranscriptions: (state) => {
      state.transcriptions = [];
      state.currentTranscription = null;
    },
    
    // Error management
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    // Reset state
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Transcribe audio
      .addCase(transcribeAudio.pending, (state) => {
        state.isTranscribing = true;
        state.error = null;
      })
      .addCase(transcribeAudio.fulfilled, (state, action) => {
        state.isTranscribing = false;
        state.transcriptions.unshift(action.payload);
        state.currentTranscription = action.payload;
        state.error = null;
      })
      .addCase(transcribeAudio.rejected, (state, action) => {
        state.isTranscribing = false;
        state.error = action.payload as string;
      })
      
      // Service health check
      .addCase(checkTranscriptionServiceHealth.pending, (_state) => {
        // Don't show loading for health checks
      })
      .addCase(checkTranscriptionServiceHealth.fulfilled, (state, action) => {
        state.serviceAvailable = action.payload.available;
        state.serviceHealthy = action.payload.healthy;
        state.lastHealthCheck = action.payload.timestamp;
      })
      .addCase(checkTranscriptionServiceHealth.rejected, (state, action) => {
        state.serviceAvailable = false;
        state.serviceHealthy = false;
        state.lastHealthCheck = new Date().toISOString();
        console.warn('Transcription service unavailable:', action.payload);
      })
      
      // Upload to Directus
      .addCase(uploadTranscriptionToDirectus.pending, (state) => {
        state.isUploading = true;
        state.error = null;
      })
      .addCase(uploadTranscriptionToDirectus.fulfilled, (state, action) => {
        state.isUploading = false;
        // Mark transcription as uploaded
        const transcription = state.transcriptions.find(t => t.id === action.payload.transcriptionId);
        if (transcription) {
          (transcription as any).uploadedAt = action.payload.uploadedAt;
          (transcription as any).evidenceId = action.payload.evidenceId;
        }
      })
      .addCase(uploadTranscriptionToDirectus.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setRecordingState,
  setLanguage,
  setPrompt,
  setAutoTranscribe,
  setCurrentTranscription,
  addTranscription,
  removeTranscription,
  clearTranscriptions,
  setError,
  clearError,
  resetState,
} = transcriptionSlice.actions;

// Selectors
export const selectTranscriptionState = (state: { transcription: TranscriptionState }) => state.transcription;
export const selectTranscriptions = (state: { transcription: TranscriptionState }) => state.transcription.transcriptions;
export const selectCurrentTranscription = (state: { transcription: TranscriptionState }) => state.transcription.currentTranscription;
export const selectIsRecording = (state: { transcription: TranscriptionState }) => state.transcription.isRecording;
export const selectIsTranscribing = (state: { transcription: TranscriptionState }) => state.transcription.isTranscribing;
export const selectIsUploading = (state: { transcription: TranscriptionState }) => state.transcription.isUploading;
export const selectTranscriptionError = (state: { transcription: TranscriptionState }) => state.transcription.error;
export const selectTranscriptionConfig = (state: { transcription: TranscriptionState }) => ({
  language: state.transcription.language,
  prompt: state.transcription.prompt,
  autoTranscribe: state.transcription.autoTranscribe,
});
export const selectServiceStatus = (state: { transcription: TranscriptionState }) => ({
  available: state.transcription.serviceAvailable,
  healthy: state.transcription.serviceHealthy,
  lastCheck: state.transcription.lastHealthCheck,
});

// Helper selectors
export const selectTranscriptionsByType = (type: 'interaction' | 'evidence' | 'visit') => 
  (state: { transcription: TranscriptionState }) => 
    state.transcription.transcriptions.filter(t => t.associatedWith?.type === type);

export const selectTranscriptionsForId = (type: 'interaction' | 'evidence' | 'visit', id: string) => 
  (state: { transcription: TranscriptionState }) => 
    state.transcription.transcriptions.filter(t => 
      t.associatedWith?.type === type && t.associatedWith?.id === id
    );

export default transcriptionSlice.reducer;